"use client";

import * as React from "react";
import { Search as SearchIcon, X } from "lucide-react";

import type { Source } from "@/components/api/fiinder-api-types";
import { Button } from "@/components/ui/button";
import { usePersonation } from "@/lib/personation-context";
import { applyLinkRegexReplaces } from "@/lib/url-utils";

function getFaviconUrl(url: string): string {
  try {
    const domain = new URL(url).hostname;
    return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=32`;
  } catch {
    return "";
  }
}

function getDomain(url: string): string {
  try {
    return new URL(url).hostname.replace("www.", "");
  } catch {
    return url;
  }
}

export type LandingSourcesSidecarProps = {
  sources: Source[];
  queries?: string[];
  onClose: () => void;
  onSourceClick: (source: Source) => void;
  onQueryClick: (query: string) => void;
};

/**
 * Landing-specific sidecar for browsing all response sources.
 *
 * Clicking a source delegates handling to the parent, which decides whether to
 * open PDF in-sidecar stack mode or navigate externally.
 */
export function LandingSourcesSidecar({
  sources,
  queries,
  onClose,
  onSourceClick,
  onQueryClick,
}: LandingSourcesSidecarProps) {
  const { labels, linkRegexReplaces } = usePersonation();

  return (
    <div
      className="h-full flex flex-col bg-background"
      data-testid="landing-sources-sidecar"
    >
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border/70 bg-background/95 backdrop-blur-sm shrink-0">
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 text-muted-foreground/70 hover:text-foreground hover:bg-muted/60"
          aria-label="Close sources"
          data-testid="landing-sources-close"
          onClick={onClose}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
        <span className="flex-1 min-w-0 truncate text-xs font-medium text-foreground/80 leading-5">
          {labels.sources.sourcesTitle.replace("{count}", String(sources.length))}
        </span>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-4">
        {queries && queries.length > 0 && (
          <div data-testid="landing-sources-queries">
            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              {labels.sources.searching}
            </p>
            <div className="flex flex-wrap gap-2">
              {queries.map((query, index) => (
                <button
                  type="button"
                  key={`${query}-${index}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-muted rounded-full text-xs hover:bg-muted/80 transition-colors cursor-pointer"
                  data-testid={`landing-sources-query-${index}`}
                  onClick={() => onQueryClick(query)}
                >
                  <SearchIcon className="h-3 w-3 text-muted-foreground" />
                  <span>{query}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div data-testid="landing-sources-list">
          <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            {labels.sources.reading}
          </p>
          <div className="space-y-2">
            {sources.map((source, index) => {
              const transformedUrl = applyLinkRegexReplaces(
                source.url,
                linkRegexReplaces,
              );
              const displayText = source.description || source.snippet;
              return (
                <button
                  key={`${source.url}-${source.title ?? ""}-${index}`}
                  type="button"
                  className="w-full text-left border rounded-lg p-3 hover:bg-muted/40 transition-colors cursor-pointer"
                  data-testid={`landing-source-card-${index}`}
                  onClick={() => onSourceClick(source)}
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <img
                      src={getFaviconUrl(transformedUrl)}
                      alt=""
                      className="w-4 h-4 rounded-sm flex-shrink-0"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                    <span className="text-[11px] text-muted-foreground truncate">
                      {getDomain(transformedUrl)}
                    </span>
                  </div>
                  <p className="text-sm font-semibold leading-tight line-clamp-2">
                    {source.title || getDomain(transformedUrl)}
                  </p>
                  {displayText && (
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                      {displayText}
                    </p>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

LandingSourcesSidecar.displayName = "LandingSourcesSidecar";
